// Where the import wizard is, in the address, and what it holds, in the tab
// (release 3.3, task Q3 of the 7 Oct plan).
//
// The steps lived only in the page's memory, so the browser's Back left the
// whole wizard and the work was gone, and a refresh started again from the
// file. Now each step is an address of its own, one history entry per step:
//
//   #/import                       Upload, before a file is read
//   #/import/<id>/upload           Upload, with the file read
//   #/import/<id>/columns          Match columns
//   #/import/<id>/values           Match values
//   #/import/<id>/review           Review and fix
//   #/import/<id>/confirm          Import
//   #/import/<id>/result           the result, which replaces Import in the
//                                  history so Back never reopens a finished
//                                  import (rule B9)
//
// Browser Back and Forward move one step and keep every answer. The answers
// are kept in sessionStorage under the import's id, so a refresh lands on the
// same step with them; it is a stop-gap until the server keeps a draft (task
// I1), and it lasts while the tab is open. An address for an import this tab
// does not hold opens Upload with one line saying why.

export const IMPORT_STEP_NAMES = ['upload', 'columns', 'values', 'review', 'confirm'] as const

export type ImportAddress = { id: string; step: number; result: boolean }

// The address of a step (1 to 5) of an import, or of its result.
export function importAddress(id: string, step: number | 'result'): string {
  if (!id) return '#/import'
  if (step === 'result') return '#/import/' + id + '/result'
  return '#/import/' + id + '/' + (IMPORT_STEP_NAMES[Math.min(Math.max(step, 1), 5) - 1] ?? 'upload')
}

// What an address names. Anything that is not an import's step reads as
// Upload with no import.
export function readImportAddress(hash: string): ImportAddress {
  const parts = hash.replace(/^#\/?/, '').split('?')[0]!.split('/').filter(Boolean)
  if (parts[0] !== 'import' || !parts[1] || !/^[0-9a-f-]{36}$/i.test(parts[1])) return { id: '', step: 1, result: false }
  const name = parts[2] ?? 'upload'
  if (name === 'result') return { id: parts[1], step: 5, result: true }
  const at = (IMPORT_STEP_NAMES as readonly string[]).indexOf(name)
  return { id: parts[1], step: at >= 0 ? at + 1 : 1, result: false }
}

const KEY = 'tprm.import.draft.'

export function saveImportDraft<T>(id: string, draft: T): void {
  if (!id) return
  try {
    sessionStorage.setItem(KEY + id, JSON.stringify(draft))
  } catch {
    // Storage refused or full: the steps still work, a refresh starts again.
  }
}

export function loadImportDraft<T>(id: string): T | null {
  if (!id) return null
  try {
    const raw = sessionStorage.getItem(KEY + id)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export function dropImportDraft(id: string): void {
  try {
    sessionStorage.removeItem(KEY + id)
  } catch {
    // Nothing to drop.
  }
}
